"use client"

import {
  resetFactsProgress,
  resetLettersProgress,
} from "@/lib/letters-progress"
import { isOnboardingCompleted } from "@/lib/onboarding"
import { getMissionMascotMiniClassName } from "@/lib/mission-mascot-mini"
import { preloadImages } from "@/lib/preload-assets"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import PlayButton from "@/components/PlayButton"
import {
  getMarkerMissionNumber,
  markerFromUrlHint,
  markerUrlCode,
} from "@/constants/MARKERS"
import { WelcomeMessage } from "@/components/WelcomeMessage"
import { startIntroAudio, stopIntroAudio } from "@/lib/game-audio"

const LOADING_DURATION_MS = 1800
const ONBOARDING_PRELOAD_ASSETS = [
  { href: "/img/onboard/bg.webp", type: "image/webp" },
  { href: "/img/onboard/cup.png", type: "image/png" },
  { href: "/img/onboard/close.png", type: "image/png" },
  { href: "/img/onboard/01.png", type: "image/png" },
  { href: "/img/onboard/02.png", type: "image/png" },
  { href: "/img/onboard/03.png", type: "image/png" },
  { href: "/img/onboard/04.png", type: "image/png" },
  { href: "/img/onboard/02-b-left.png", type: "image/png" },
  { href: "/img/onboard/03-b-left.png", type: "image/png" },
  { href: "/img/onboard/03-b-right.png", type: "image/png" },
]

export default function HomePage() {
  const router = useRouter()
  const [progress, setProgress] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const [missionNumber, setMissionNumber] = useState<number | null>(null)
  const [mascotMini, setMascotMini] = useState<string | null>(null)
  const [markerParam, setMarkerParam] = useState<string | null>(null)

  useEffect(() => {
    startIntroAudio()
    return stopIntroAudio
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const marker = markerFromUrlHint(params.get("marker"))
    if (marker) {
      setMarkerParam(markerUrlCode(marker))
      setMissionNumber(getMarkerMissionNumber(marker.id))
      setMascotMini(marker.mascotMini)
    }
    const resetTarget = params.get("reset")

    if (resetTarget === "all") {
      resetLettersProgress()
      resetFactsProgress()
    } else if (resetTarget === "letters") {
      resetLettersProgress()
    } else if (resetTarget === "facts") {
      resetFactsProgress()
    }

    if (!isOnboardingCompleted()) {
      preloadImages(ONBOARDING_PRELOAD_ASSETS)
    }
  }, [])

  useEffect(() => {
    if (!isReady || !markerParam) return
    router.replace(
      isOnboardingCompleted()
        ? `/game?marker=${markerParam}&source=qr`
        : `/onboarding?marker=${markerParam}&source=qr`
    )
  }, [isReady, markerParam, router])

  useEffect(() => {
    const startedAt = Date.now()

    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startedAt
      const nextProgress = Math.min(
        100,
        Math.round((elapsed / LOADING_DURATION_MS) * 100)
      )

      setProgress(nextProgress)

      if (nextProgress >= 100) {
        window.clearInterval(intervalId)
        setIsReady(true)
      }
    }, 30)

    return () => window.clearInterval(intervalId)
  }, [])

  const handlePlayClick = () => {
    router.replace(isOnboardingCompleted() ? "/scan" : "/onboarding")
  }

  return (
    <main className="relative flex min-h-dvh flex-col overflow-hidden bg-[#061b32] text-white">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/img/start/bg.webp')" }}
      />

      <div className="absolute inset-0 bg-black/10" />

      <img
        src="/img/start/logos.png"
        alt=""
        className="absolute left-1/2 top-[11px] translate-x-[-50%] w-[208px] ml-[-10px] md:w-[408px]"
      />

      <section className="absolute top-1/2 left-0 w-full translate-y-[-50%] z-10 flex flex-col items-center text-center pt-[10dvh]">
        <WelcomeMessage />

        {missionNumber !== null ? (
          <div
            className="relative w-[224px] h-[87px] overflow-hidden bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: "url('/img/start/mission.png')" }}
          >
            <span className="text-[31px] font-bold w-[30px] leading-[100%] text-center relative inline-block mt-[26px] ml-[26px]">
              {missionNumber}
            </span>
            {mascotMini ? (
              <img
                src={mascotMini}
                alt=""
                className={getMissionMascotMiniClassName(mascotMini)}
              />
            ) : null}
          </div>
        ) : null}
      </section>

      <div className="start-footer absolute bottom-[40px] left-0 w-full">
        {!isReady ? (
          <>
            <p className="mb-[14px] text-[14px] md:text-[18px] text-white/95 text-center">
              Подожди, игра загружается
            </p>

            <div className="mx-auto h-[21px] w-[82%] mb-[12px] rounded-full border border-cyan-200/50 bg-[#071449]/75 shadow-[inset_0_2px_8px_rgba(255,255,255,0.18),0_6px_22px_rgba(0,0,0,0.28)]">
              <div
                className="relative h-full rounded-full bg-[linear-gradient(90deg,rgba(38,211,229,0.95)_0%,rgba(70,156,221,0.9)_50%,rgba(10,22,86,0.9)_100%)] transition-[width] duration-100 ease-out"
                style={{ width: `${progress}%` }}
              >
                <img
                  src="/img/start/progress-cup.png"
                  alt=""
                  className="absolute top-1/2 right-0 translate-x-[16px] translate-y-[-50%] w-[48px]"
                />
              </div>
            </div>
          </>
        ) : (
          <PlayButton onClick={handlePlayClick} text="играть" type={1} />
        )}
      </div>

    </main>
  )
}
